// Shopify's token endpoint returns `expires_in` as a duration in seconds.
// NextAuth's first login runs that through openid-client's TokenSet, which
// stores `expires_at` as an absolute unix timestamp in seconds. The refresh
// response does not, so callers must convert it themselves.
//
// A value below this floor is a duration (access tokens live for minutes or
// hours). A value at or above it is an absolute timestamp. The floor is
// 2001-09-09 in unix seconds, which no token lifetime will reach.
const ABSOLUTE_EXPIRY_FLOOR_S = 1_000_000_000;

// Refresh while the access token still has time left, so the request that
// follows this check does not arrive at the API with an already-expired JWT.
export const REFRESH_SKEW_MS = 60 * 1000;

export const REFRESH_ACCESS_TOKEN_ERROR = 'RefreshAccessTokenError';
export const CUSTOMER_UUID_ERROR = 'CustomerUUIDError';

const finiteNumber = value => {
  if (typeof value === 'string' && value.trim() !== '') {
    value = Number(value);
  }
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return null;
  }
  return value;
};

const isAbsoluteUnixSeconds = value => {
  const parsed = finiteNumber(value);
  return parsed !== null && parsed >= ABSOLUTE_EXPIRY_FLOOR_S;
};

/**
 * Absolute access-token expiry in unix seconds, or null when it cannot be derived.
 * An absolute `expiresAt` wins. Otherwise `expiresIn` is a duration added to now.
 * A duration stored in `expiresAt` is ignored here: the caller refreshes once
 * and replaces it, because the moment that duration was written is unknown.
 */
export const absoluteExpiresAt = ({
  expiresAt,
  expiresIn,
  now = Date.now(),
} = {}) => {
  if (isAbsoluteUnixSeconds(expiresAt)) {
    return finiteNumber(expiresAt);
  }

  const duration = finiteNumber(expiresIn);
  if (duration === null || duration <= 0) {
    return null;
  }

  return Math.floor(now / 1000) + duration;
};

/**
 * True when the Shopify access token should be refreshed before it is used.
 * Unknown expiry returns false: treating it as expired refreshes on every
 * session read. A duration left in `expiresAt` by the old refresh bug returns
 * true so that session refreshes once and is rewritten as an absolute timestamp.
 */
export const accessTokenNeedsRefresh = (expiresAt, now = Date.now()) => {
  const parsed = finiteNumber(expiresAt);
  if (parsed === null) {
    return false;
  }
  if (!isAbsoluteUnixSeconds(parsed)) {
    return true;
  }
  return now >= parsed * 1000 - REFRESH_SKEW_MS;
};
